/**
 * Canvas on the phone, without the phone ever holding the Canvas key.
 *
 * A Canvas user token is full account access — read grades, submit work,
 * change settings. This page's whole authorization model is temporary Cognito
 * credentials handed to a browser, so a token the page could fetch would be a
 * token anyone with those credentials owns outright. So nothing here ever sees
 * one: it sends the Cognito ID token it already has, and the API sends back
 * Canvas data.
 *
 * There is one failure worth naming separately. Renewing Canvas access needs a
 * browser someone has signed in to, on a desktop that may be switched off —
 * nothing in the cloud can do it. So an expired token is not a retry, and this
 * must not offer one.
 */

export class CanvasExpired extends Error {
  constructor(message) {
    super(message || 'Canvas access has run out.');
    this.name = 'CanvasExpired';
    // What the UI keys off. A retry button here would be a lie: pressing it
    // again cannot possibly work, because the fix is on a different machine.
    this.needsPC = true;
  }
}

export class CanvasUnavailable extends Error {
  constructor(message) {
    super(message || 'Could not reach Canvas.');
    this.name = 'CanvasUnavailable';
    this.needsPC = false;
  }
}

async function read(config, session, path, fetchImpl = fetch) {
  if (!config?.canvasApiUrl) {
    throw new CanvasUnavailable('Canvas is not set up for this site yet.');
  }

  let response;
  try {
    response = await fetchImpl(`${config.canvasApiUrl}${path}`, {
      headers: { authorization: `Bearer ${session.idToken}` },
    });
  } catch (cause) {
    throw new CanvasUnavailable('No connection.');
  }

  if (response.status === 401 || response.status === 403) {
    // The Cognito token, not the Canvas one — the sign-in here has lapsed.
    throw new CanvasUnavailable('Your sign-in has expired. Sign in again.');
  }

  let body = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  if (response.status === 503 && body?.error === 'token_expired') {
    throw new CanvasExpired(body.message);
  }
  if (!response.ok) {
    throw new CanvasUnavailable('Canvas did not answer.');
  }
  return body;
}

export async function upcoming(config, session, fetchImpl) {
  const body = await read(config, session, '/canvas/upcoming', fetchImpl);
  return body?.upcoming ?? [];
}

export async function courses(config, session, fetchImpl) {
  const body = await read(config, session, '/canvas/courses', fetchImpl);
  return body?.courses ?? [];
}

export async function assignments(config, session, { start, end }, fetchImpl) {
  const query = `?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
  const body = await read(config, session, `/canvas/assignments${query}`, fetchImpl);
  return body?.assignments ?? [];
}

/** What to put on screen for a failure, and whether a retry is honest. */
export function describeCanvasError(error) {
  if (error?.needsPC) {
    return {
      message: 'Canvas access has run out. It needs your PC to sign in again.',
      canRetry: false,
    };
  }
  return {
    message: error?.message || 'Could not reach Canvas.',
    canRetry: true,
  };
}
